import * as Sentry from '@sentry/react';
import SessionManager from './utils/sessionManager';

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  release: process.env.REACT_APP_VERSION,

  // Route events through our own Cloudflare Worker so ad blockers / privacy
  // extensions don't drop requests to *.ingest.sentry.io. See workers/sentry-proxy/.
  tunnel: 'https://sentry-proxy.hippo.co.za',

  // Capture full PII (IP address, request headers/cookies, user context) —
  // no client-side anonymisation.
  sendDefaultPii: true,

  // Keep nested context readable. At the default depth of 3, the API request
  // and response bodies attached in apiServices.js arrive as "[Object]", which
  // hides validation messages and the fields that were sent.
  normalizeDepth: 6,

  // Identify events by the visitor's session id, the SessionID header every
  // API call sends. Events all arrive through the tunnel from one Cloudflare
  // IP, so IP-based user counts read 1. Read at send time, so it follows the
  // session if it changes, and absent until the first API call mints one.
  beforeSend(event) {
    const sessionId = SessionManager.peekSessionId();
    if (sessionId) {
      event.user = { ...event.user, id: sessionId };
      event.tags = { ...event.tags, session_id: sessionId };
    }
    return event;
  },

  // Errors from code that isn't ours: in-app browsers' native bridges and
  // injected scripts, a link scanner, browser extensions and a service worker
  // we don't ship. None of these strings appear in src/. Custom message
  // filters in Sentry itself need the Business plan, so they live here.
  ignoreErrors: [
    /Error invoking \w+: Java /, // Android in-app browser bridges
    /window\.webkit\.messageHandlers/, // iOS in-app browser bridges
    "reading 'domInteractive'", // TikTok in-app browser
    '_pcmBridgeCallbackHandler', // in-app browser bridge
    /\b(?:x|sw)browser is not defined/, // in-app browser injection
    'JS_GET_ENTITIES', // in-app browser injection
    /Could not load "\w+"/, // Google Maps failing to load one of its modules
    /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/, // Outlook Safe Links scanner
    'runtime.sendMessage', // browser extensions
    'relatedpush_sw.js', // push service worker registered by a third party
  ],

  // Errors whose innermost stack frame is in someone else's script.
  denyUrls: [
    /^iabjs:\/\//, // Facebook in-app browser scripts
    /googletagmanager\.com/, // GTM tags (marketing owns these)
    /clarity\.js/, // Microsoft Clarity
  ],

  integrations: [
    // React Router v7 in declarative mode (<BrowserRouter> + <Routes>)
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      maskAllInputs: false,
      blockAllMedia: false,
      // Don't pull a session replay for API/backend failures. Those are still
      // sent to Sentry for alerting (see apiServices.js), but they're tagged
      // with `api_endpoint`, so we skip replay sampling for them here. Return
      // false = ignore this error for replay; true = let replaysOnErrorSampleRate
      // decide. Result: replays only accompany real errors thrown to the screen.
      beforeErrorSampling: (event) => !event.tags?.api_endpoint,
    }),
  ],

  // Tracing
  tracesSampleRate: 0.1, // capture a performance transaction for 10% of sessions
  tracePropagationTargets: ['localhost', /^https:\/\/api\.hippo\.co\.za/],

  // Session Replay — only ever capture a replay when a real error is thrown to
  // the screen; never record ordinary (non-error) sessions.
  replaysSessionSampleRate: 0, // no always-on / random session replays
  replaysOnErrorSampleRate: 1.0, // capture the session on every real error
});
